/**
 * The cursor through the review queue: where the reviewer lands after each
 * decision.
 *
 * The whole point of the queue is speed: read, decide, and be looking at the
 * next one without having chosen to be. That means the list shrinks under the
 * cursor, and getting the arithmetic wrong is the difference between a tool you
 * can work through and one that makes you re-find your place every time.
 *
 * Deciding on an item REMOVES it, so "stay where you are" already means "move
 * to the next one" — the index does not change, the thing under it does. The
 * only case that needs thought is the end of the list, where holding the index
 * would leave it pointing past the end.
 *
 * Pure and dependency-free so jest can reach it, per this repo's CRA/router
 * constraint.
 */
import type { ReviewItem } from '../types';

export interface QueueCursor {
  items: ReviewItem[];
  index: number;
}

/**
 * Remove the page just decided on and work out what to look at next.
 *
 * Removal is BY ID, not by index. The queue can be refetched between render and
 * decision, and an index into a list that has since changed is how you approve
 * the wrong page — the one failure this screen must never have.
 */
export function afterDecision(
  items: ReviewItem[],
  pageId: number,
  index: number,
): QueueCursor {
  const at = items.findIndex((i) => i.page_id === pageId);
  if (at === -1) return { items, index: clamp(index, items.length) };
  const next = items.filter((i) => i.page_id !== pageId);
  // Removing something BEFORE the cursor shifts everything down under it, so
  // the index has to come back one to keep looking at the same page.
  const moved = at < index ? index - 1 : index;
  return { items: next, index: clamp(moved, next.length) };
}

/** Keep the cursor inside the list. An empty list sits at 0, not at -1. */
export function clamp(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(Math.max(index, 0), length - 1);
}

/** Move by one, without wrapping. Wrapping in a work queue silently sends you
 *  back over pages you have already looked at. */
export function step(index: number, delta: number, length: number): number {
  return clamp(index + delta, length);
}

/**
 * Which pages' bodies are worth having in hand.
 *
 * The current one and the next one. Fetching the next while the reviewer reads
 * the current is what makes the queue feel instant instead of a spinner between
 * every decision; fetching further ahead buys nothing, because a decision takes
 * longer than a request.
 */
export function toPrefetch(items: ReviewItem[], index: number): ReviewItem[] {
  return [items[index], items[index + 1]].filter(
    (i): i is ReviewItem => i !== undefined,
  );
}

/** What the queue's emptiness means, which is not always "well done". */
export type EmptyReason = 'caught-up' | 'review-not-enabled' | 'site-caught-up';

export function emptyReason(opts: {
  anySiteRequiresReview: boolean;
  siteFilter: number | null;
}): EmptyReason {
  // An account with review switched off everywhere shows an empty queue that
  // looks exactly like a finished one. Telling somebody "all caught up" when
  // the feature was never turned on is how a setting stays undiscovered.
  if (!opts.anySiteRequiresReview) return 'review-not-enabled';
  return opts.siteFilter ? 'site-caught-up' : 'caught-up';
}

/**
 * The queue re-read while someone is working through it. Pure.
 *
 * Pages that became ready since go on the end; pages decided elsewhere (the
 * growth agent reviews too) leave, EXCEPT the one on screen, which stays put
 * so nobody's place moves under them. A plain reload jumped the cursor and
 * blanked the page mid-read.
 */
export function mergeQueue(
  items: ReviewItem[],
  index: number,
  fresh: ReviewItem[],
): { items: ReviewItem[]; index: number } {
  const current = items[index];
  const freshIds = new Set(fresh.map((i) => i.page_id));
  const kept = items.filter((i) => freshIds.has(i.page_id) || i.page_id === current?.page_id);
  const known = new Set(kept.map((i) => i.page_id));
  const merged = [...kept, ...fresh.filter((i) => !known.has(i.page_id))];
  const at = current ? merged.findIndex((i) => i.page_id === current.page_id) : 0;
  return { items: merged, index: at < 0 ? 0 : at };
}
